import produce from 'immer';
import type { WritableDraft } from 'immer/dist/internal';
import { MetricEntry, MetricEntryBehavior } from 'common/query-builder/query-entry-section/types';
import { FunnelOrder } from 'common/types/reports/bookmark';
import { SVG_ICONS } from 'common/components/svg-icon/icons';
import type { RowProps } from '../catalog';

/**
 * Returns true if the behavior at the given index is set to FunnelOrder.Any.
 * @param index
 * @returns boolean
 */
const checkBehaviorIsAnyOrder = (entry: MetricEntry | WritableDraft<MetricEntry>, index: number): boolean => {
  var _entry$behavior$behav;
  return ((_entry$behavior$behav = entry.behavior.behaviors) === null || _entry$behavior$behav === void 0 ? void 0 : _entry$behavior$behav[index].funnelOrder) === FunnelOrder.Any;
};
function toggleBehaviorAnyOrder(behavior: WritableDraft<MetricEntryBehavior>): WritableDraft<MetricEntryBehavior> {
  behavior.funnelOrder = behavior.funnelOrder === FunnelOrder.Any ? FunnelOrder.Loose : FunnelOrder.Any;
  return behavior;
}

/**
 * Toggles the funnel order type for the given behavior at the given index
 * between FunnelOrder.Any and FunnelOrder.Loose.
 *
 * NOTE: Toggling the funnel order for a nested event comparison behavior just
 * toggles it on the parent behavior.
 * @param index
 * @returns void
 */
function _toggleAnyOrder(_ref: {
  entry: MetricEntry;
  index?: number;
  onUpdate: RowProps[`updateEntry`];
}): void {
  let {
    entry,
    index,
    onUpdate
  }: {
    entry: MetricEntry;
    index?: number;
    onUpdate: RowProps[`updateEntry`];
  } = _ref;
  const updatedEntry = produce(entry, draft => {
    var _draft$behavior$funne;
    if (!draft.behavior.behaviors) {
      return draft;
    }
    const initialOrder = (_draft$behavior$funne = draft.behavior.funnelOrder) !== null && _draft$behavior$funne !== void 0 ? _draft$behavior$funne : FunnelOrder.Loose;

    // toggles a specific step order
    if (index !== undefined && draft.behavior.behaviors[index]) {
      draft.behavior.behaviors[index] = toggleBehaviorAnyOrder(draft.behavior.behaviors[index]);
    } else {
      // toggles all behaviors in the funnel
      draft.behavior.behaviors = draft.behavior.behaviors.map((behavior, behaviorIndex) => {
        const updatedOrder = initialOrder === FunnelOrder.Any ? FunnelOrder.Loose : FunnelOrder.Any;
        behavior.funnelOrder = behaviorIndex === 0 ? FunnelOrder.Loose : updatedOrder;
        return behavior;
      });
    }

    // If all behaviors are Loose then the parent behavior should be Loose otherwise
    // it should be Any.
    // @todo(metrics): This is handled on the backend by transform_multi_metric_query_params_to_funnels_bookmark.
    // Can we just remove all client code and rely on backend to compute this?
    draft.behavior.funnelOrder = draft.behavior.behaviors.every(behavior => behavior.funnelOrder === FunnelOrder.Loose) ? FunnelOrder.Loose : FunnelOrder.Any;
    return draft;
  });
  onUpdate(updatedEntry);
}
export { _toggleAnyOrder as toggleAnyOrder };
export function useFunnelAnyOrderIconToggle(entry: MetricEntry, behaviorIndex: number, onUpdate: RowProps[`updateEntry`]): {
  toggleAnyOrder: () => void;
  rowIcon: keyof typeof SVG_ICONS | null;
} {
  const behaviorIsAnyOrder = checkBehaviorIsAnyOrder(entry, behaviorIndex);
  return {
    toggleAnyOrder: () => _toggleAnyOrder({
      entry,
      index: behaviorIndex,
      onUpdate
    }),
    rowIcon: behaviorIsAnyOrder ? "behavior-event-any-order" : null
  };
}